import { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Form,
  Input,
  Modal,
  Typography,
  Tabs,
  Popconfirm,
  App,
  Empty,
} from 'antd';
import { PlusOutlined, DeleteOutlined, SwapRightOutlined } from '@ant-design/icons';
import {
  getManufacturerAliases,
  addManufacturerAlias,
  deleteManufacturerAlias,
  getCategoryAliases,
  addCategoryAlias,
  deleteCategoryAlias,
  type Alias,
  type AliasCreateRequest,
} from '../../api/settings';

const { Text, Paragraph } = Typography;

interface AliasTableProps {
  type: 'manufacturer' | 'category';
  aliases: Alias[];
  loading: boolean;
  onAdd: (data: AliasCreateRequest) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

function AliasTable({ type, aliases, loading, onAdd, onDelete }: AliasTableProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm<AliasCreateRequest>();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values: AliasCreateRequest) => {
    setSubmitting(true);
    try {
      await onAdd(values);
      form.resetFields();
      setIsModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Alias (Variant Name)',
      dataIndex: 'alias_name',
      key: 'alias_name',
      width: '40%',
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '',
      key: 'arrow',
      width: 50,
      render: () => <SwapRightOutlined style={{ color: '#999' }} />,
    },
    {
      title: 'Canonical (Display Name)',
      dataIndex: 'canonical_name',
      key: 'canonical_name',
      width: '40%',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: Alias) => (
        <Popconfirm
          title="Delete this alias?"
          description="Products using this alias name will show separately again."
          onConfirm={() => onDelete(record.id)}
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  const typeLabel = type === 'manufacturer' ? 'Manufacturer' : 'Category';

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary">
          {aliases.length} alias{aliases.length !== 1 ? 'es' : ''} configured
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>
          Add Alias
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={aliases}
        rowKey="id"
        loading={loading}
        pagination={aliases.length > 10 ? { pageSize: 10 } : false}
        locale={{
          emptyText: (
            <Empty
              description={`No ${typeLabel.toLowerCase()} aliases configured`}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />

      <Modal
        title={`Add ${typeLabel} Alias`}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Map a variant name to a canonical name. When searching, the variant will be grouped with the canonical name.
        </Paragraph>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="alias_name"
            label="Alias (Variant Name)"
            rules={[{ required: true, message: 'Enter the variant name to map from' }]}
            tooltip="The name as it appears in the database (e.g., 'Juniper-IMSourcing')"
          >
            <Input placeholder="e.g., Juniper-IMSourcing" />
          </Form.Item>

          <Form.Item
            name="canonical_name"
            label="Canonical (Display Name)"
            rules={[{ required: true, message: 'Enter the canonical name to map to' }]}
            tooltip="The preferred name to display (e.g., 'Juniper Networks, Inc')"
          >
            <Input placeholder="e.g., Juniper Networks, Inc" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Add Alias
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export function AliasesTab() {
  const { message } = App.useApp();
  const [manufacturerAliases, setManufacturerAliases] = useState<Alias[]>([]);
  const [categoryAliases, setCategoryAliases] = useState<Alias[]>([]);
  const [loadingManufacturers, setLoadingManufacturers] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const fetchManufacturerAliases = async () => {
    setLoadingManufacturers(true);
    try {
      const data = await getManufacturerAliases();
      setManufacturerAliases(data);
    } catch (error) {
      console.error('Failed to fetch manufacturer aliases:', error);
      message.error('Failed to load manufacturer aliases');
    } finally {
      setLoadingManufacturers(false);
    }
  };

  const fetchCategoryAliases = async () => {
    setLoadingCategories(true);
    try {
      const data = await getCategoryAliases();
      setCategoryAliases(data);
    } catch (error) {
      console.error('Failed to fetch category aliases:', error);
      message.error('Failed to load category aliases');
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchManufacturerAliases();
    fetchCategoryAliases();
  }, []);

  const handleAddManufacturerAlias = async (data: AliasCreateRequest) => {
    try {
      const response = await addManufacturerAlias(data);
      if (response.status === 'success') {
        message.success(`Alias added: "${data.alias_name}" → "${data.canonical_name}"`);
        fetchManufacturerAliases();
      } else {
        message.error(response.error || 'Failed to add alias');
      }
    } catch (error: unknown) {
      console.error('Failed to add manufacturer alias:', error);
      const err = error as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || 'Failed to add alias');
    }
  };

  const handleDeleteManufacturerAlias = async (id: number) => {
    try {
      await deleteManufacturerAlias(id);
      message.success('Alias deleted');
      fetchManufacturerAliases();
    } catch (error) {
      console.error('Failed to delete manufacturer alias:', error);
      message.error('Failed to delete alias');
    }
  };

  const handleAddCategoryAlias = async (data: AliasCreateRequest) => {
    try {
      const response = await addCategoryAlias(data);
      if (response.status === 'success') {
        message.success(`Alias added: "${data.alias_name}" → "${data.canonical_name}"`);
        fetchCategoryAliases();
      } else {
        message.error(response.error || 'Failed to add alias');
      }
    } catch (error: unknown) {
      console.error('Failed to add category alias:', error);
      const err = error as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || 'Failed to add alias');
    }
  };

  const handleDeleteCategoryAlias = async (id: number) => {
    try {
      await deleteCategoryAlias(id);
      message.success('Alias deleted');
      fetchCategoryAliases();
    } catch (error) {
      console.error('Failed to delete category alias:', error);
      message.error('Failed to delete alias');
    }
  };

  const tabItems = [
    {
      key: 'manufacturers',
      label: `Manufacturers (${manufacturerAliases.length})`,
      children: (
        <AliasTable
          type="manufacturer"
          aliases={manufacturerAliases}
          loading={loadingManufacturers}
          onAdd={handleAddManufacturerAlias}
          onDelete={handleDeleteManufacturerAlias}
        />
      ),
    },
    {
      key: 'categories',
      label: `Categories (${categoryAliases.length})`,
      children: (
        <AliasTable
          type="category"
          aliases={categoryAliases}
          loading={loadingCategories}
          onAdd={handleAddCategoryAlias}
          onDelete={handleDeleteCategoryAlias}
        />
      ),
    },
  ];

  return (
    <Card>
      <Paragraph type="secondary">
        Configure name aliases to merge duplicate manufacturer and category names in search autocomplete.
        Variants (aliases) will be grouped under their canonical (display) name.
      </Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        <Text strong>Note:</Text> Common suffixes like "Inc.", "LLC", "Corp." are automatically normalized.
        Use aliases for cases that cannot be detected automatically (e.g., "Juniper-IMSourcing" → "Juniper Networks").
      </Paragraph>

      <Tabs items={tabItems} />
    </Card>
  );
}
